import { pluralize } from "@entrilia/shared/utilities/stringHelper.ts";
import FilterListRoundedIcon from "@mui/icons-material/FilterListRounded";
import { Badge, Box, Tooltip } from "@mui/material";
import { useCallback, useState } from "react";
import type { FieldConfiguration } from "../../../../../../../../api/types/fieldConfigurationTypes.ts";
import type { ConditionField, DimensionDescriptor, ReportField } from "../../../../../../../../api/types/svTypes.ts";
import CustomConditions from "../values/CustomConditions.tsx";
import AreaFieldItem from "./area-fields/AreaFieldItem.tsx";
import FieldItemActionButton from "./area-fields/FieldItemActionButton.tsx";
import type { FieldItemProps } from "./area-fields/areaFiledItem.types.ts";

interface Props<T> extends FieldItemProps<T> {
  dimensions: DimensionDescriptor[];
  globalConditions: ConditionField[];
  canToggleParameter: boolean;
  isReadonlyCustomLabel: boolean;
  updateField: ((field: ReportField, change: Partial<FieldConfiguration>) => void) | undefined;
  detailedView?: boolean;
  supportsCustomConditions: boolean;
}

const FieldItemWithConditions = <T extends ReportField>({
  dimensions,
  globalConditions,
  canToggleParameter,
  isReadonlyCustomLabel,
  updateField,
  detailedView,
  supportsCustomConditions,
  children,
  ...fieldProps
}: Props<T>) => {
  const [conditionsAnchorEl, setConditionsAnchorEl] = useState<HTMLElement>();
  const handleConditionsButtonRef = useCallback((element: HTMLButtonElement | null) => {
    if (!element) {
      setConditionsAnchorEl(undefined);
    }
  }, []);
  const { field, canBeRemoved, autoShowItemOptionsId, onSetAutoShowItemOptionsId, readonly } = fieldProps;
  const conditionsEnabled = supportsCustomConditions && field.config.useCustomConditions;
  const showConditionsButton = detailedView !== undefined && supportsCustomConditions;
  const conditionsCount = (field.config.customConditions?.length ?? 0) + (field.config.linkedConditions?.length ?? 0);

  return (
    <>
      <Box
        sx={{
          display: "flex",
          width: "100%",
          alignItems: "center",
          "&:has(.field-remove-button):is(:hover, :has(:focus-visible)) .field-subcaption": {
            display: "none",
          },
        }}
      >
        {children}
        <AreaFieldItem {...fieldProps}>
          {showConditionsButton && (
            <Tooltip arrow title={`${conditionsCount} ${pluralize("condition", conditionsCount)}`} disableInteractive>
              <FieldItemActionButton
                ref={handleConditionsButtonRef}
                aria-label="Conditions"
                aria-haspopup="dialog"
                aria-expanded={!!conditionsAnchorEl}
                alwaysVisible={conditionsCount > 0 || !!conditionsAnchorEl}
                color={conditionsCount > 0 ? "primary" : "secondary"}
                onClick={(event) => setConditionsAnchorEl(event.currentTarget)}
              >
                <Badge color="primary" variant="dot" invisible={detailedView || conditionsCount === 0}>
                  <FilterListRoundedIcon />
                </Badge>
              </FieldItemActionButton>
            </Tooltip>
          )}
        </AreaFieldItem>
      </Box>
      {(showConditionsButton || conditionsEnabled) && (
        <CustomConditions
          field={field}
          dimensions={dimensions}
          globalConditions={globalConditions}
          canBeRemoved={canBeRemoved}
          updateField={updateField}
          canToggleParameter={canToggleParameter}
          isReadonlyCustomLabel={isReadonlyCustomLabel}
          autoShowItemOptionsId={autoShowItemOptionsId}
          onSetAutoShowItemOptionsId={onSetAutoShowItemOptionsId}
          readonly={readonly}
          detailedView={detailedView}
          popupAnchorEl={conditionsAnchorEl}
          onClosePopup={() => setConditionsAnchorEl(undefined)}
        />
      )}
    </>
  );
};

export default FieldItemWithConditions;
